import { safeParseFloat } from "@lib/TypeUtil"
import { Attachment } from "@model/Attachment"
import { Contact } from "@model/contacts/Contact"
import { HistoryItem } from "@model/HistoryItem"
import { ServiceCode, ServiceRequestType } from "@model/serviceRequests/ServiceRequest"
import { ServiceSchedule } from "@model/ServiceSchedule"
import { Step } from "@model/Step"
import { Task } from "@model/Task"
import { WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"

export type ServiceItemForm = {
    uiElementId: number
    id: number | null
    name: string
    code: string
    laborCost: string | null
    partsCost: string | null
    otherCost: string | null
    mechanic: string
    laborHours: number | null
    laborMinutes: number | null
    notes: string
    attachments: Attachment[]
    isSaving: boolean
    isLoading: boolean
    serviceCodes: ServiceCode[]
    serviceCode: ServiceCode | null
    contacts: Contact[]
    mechanics: Contact[]
    filesId: string | null
    assetId: number | null
    isSaved: boolean
    dismissedReason: string
    status: WorkRequestStatus
    workRequestType: WorkRequestType
    dueDate: Date | null
    dueOdometer: number | null
    dueHourMeter: number | null
    hoursUntil: number | null
    milesUntil: number | null
    daysUntil: number | null
    step: Step | null
    initialStep: Step | null
    areTasksLoading: boolean
    schedule: ServiceSchedule | null
    tasks: Task[] | null
    initialTasks: Task[] | null
    notifyContacts: number[]
    // Action Item 6767: Work Performed By lives at Service Item level (multi-select), moved down
    // from the Work Order level for M2.5.
    workPerformedBy: number[]
    workOrderId: number | null
    serviceScheduleStepId: number | null
    serviceSubTypeId: number | null
    serviceType: ServiceRequestType | null
    history: HistoryItem<WorkRequestStatus>[]
    historyIsLoading: boolean
    followUpWorkRequestId: number | null
    childFollowUpId: number | null
}

export function newServiceItemForm(uiElementId: number): ServiceItemForm {
    return {
        uiElementId,
        id: null,
        name: "",
        code: "",
        laborCost: null,
        partsCost: null,
        otherCost: null,
        mechanic: "Unassigned",
        laborHours: null,
        laborMinutes: null,
        notes: "",
        attachments: [],
        isSaving: false,
        isLoading: false,
        serviceCodes: [],
        serviceCode: null,
        mechanics: [],
        contacts: [],
        filesId: null,
        assetId: null,
        isSaved: false,
        dismissedReason: "",
        status: WorkRequestStatus.ToDo,
        workRequestType: WorkRequestType.Service,
        dueDate: null,
        dueOdometer: null,
        dueHourMeter: null,
        hoursUntil: null,
        milesUntil: null,
        daysUntil: null,
        step: null,
        initialStep: null,
        areTasksLoading: false,
        schedule: null,
        tasks: null,
        initialTasks: null,
        notifyContacts: [],
        workPerformedBy: [],
        workOrderId: null,
        serviceScheduleStepId: null,
        serviceSubTypeId: null,
        serviceType: null,
        history: [],
        historyIsLoading: false,
        followUpWorkRequestId: null,
        childFollowUpId: null,
    }
}

export function compareObjectServiceItemForm(form: ServiceItemForm): ServiceItemForm {
    return {
        assetId: form.assetId,
        uiElementId: form.uiElementId,
        id: form.id,
        name: form.name,
        code: form.code,
        laborCost: form.laborCost,
        partsCost: form.partsCost,
        otherCost: form.otherCost,
        mechanic: form.mechanic,
        laborHours: form.laborHours,
        laborMinutes: form.laborMinutes,
        notes: form.notes,
        attachments: form.attachments,
        serviceCode: form.serviceCode,
        filesId: form.filesId,
        dismissedReason: form.dismissedReason,
        status: form.status,
        workRequestType: form.workRequestType,
        dueDate: form.dueDate,
        dueOdometer: form.dueOdometer,
        dueHourMeter: form.dueHourMeter,
        hoursUntil: form.hoursUntil,
        milesUntil: form.milesUntil,
        daysUntil: form.daysUntil,
        step: form.step,
        schedule: form.schedule,
        tasks: form.tasks,
        notifyContacts: form.notifyContacts,
        workPerformedBy: form.workPerformedBy,
        workOrderId: form.workOrderId,
        serviceScheduleStepId: form.serviceScheduleStepId,
        serviceSubTypeId: form.serviceSubTypeId,
        serviceType: form.serviceType,
        history: [],
        historyIsLoading: false,
        areTasksLoading: false,
        followUpWorkRequestId: form.followUpWorkRequestId,
        childFollowUpId: null,
        isSaving: false,
        isLoading: false,
        serviceCodes: [],
        mechanics: [],
        contacts: [],
        isSaved: false,
        initialTasks: null,
        initialStep: null,
    }
}

export type ServiceItemsListForm = {
    items: ServiceItemForm[]
}

export type ServiceItemTotals = {
    labor: number
    parts: number
    misc: number
    total: number
}

/**
 * A cost of 0 is a real answer, so only null and blank count as "not entered". The form holds these
 * as strings, which is why this is not a numeric check.
 */
export function serviceItemHasCostValue(value: string | null): boolean {
    return value !== null && value.trim() !== ""
}

/**
 * Action Item 10811: the actual cost fields have no default value. A field the user never filled in
 * maps to null - never 0 - so it is not persisted or displayed as an auto-populated zero.
 */
export function serviceItemCostOrNull(value: string | null): number | null {
    return serviceItemHasCostValue(value) ? safeParseFloat(value) : null
}

/**
 * Action Item 10811: Actual Labor Hours is two inputs (h + m) holding one decimal value. Null when
 * neither was entered; a value in either one counts, so 0h 30m stays a real entry.
 */
export function serviceItemDecimalHoursOrNull(hours: number | null, minutes: number | null): number | null {
    if (hours === null && minutes === null) return null
    return (hours ?? 0) + (minutes ?? 0) / 60
}

/** Shown wherever a Service Item field the user never filled in is displayed read-only. */
export const serviceItemEmptyFieldLabel = "-"

/**
 * Action Item 10811: Actual Labor Hours has no default value, so an item with neither input filled
 * in reads as "-" rather than "0h 0m".
 */
export function serviceItemLaborTimeLabel(item: Pick<ServiceItemForm, "laborHours" | "laborMinutes">): string {
    if (item.laborHours === null && item.laborMinutes === null) return serviceItemEmptyFieldLabel
    return `${item.laborHours ?? 0}h ${item.laborMinutes ?? 0}m`
}

/** Actual labor + parts + misc cost for a single service item. */
export function serviceItemTotalCost(item: ServiceItemForm): number {
    return safeParseFloat(item.laborCost) + safeParseFloat(item.partsCost) + safeParseFloat(item.otherCost)
}

/** Actual labor/parts/misc cost totals for a list of service items. */
export function serviceItemsTotals(items: ServiceItemForm[]): ServiceItemTotals {
    const totals = items.reduce(
        (acc, item) => {
            acc.labor += safeParseFloat(item.laborCost)
            acc.parts += safeParseFloat(item.partsCost)
            acc.misc += safeParseFloat(item.otherCost)
            return acc
        },
        { labor: 0, parts: 0, misc: 0, total: 0 } as ServiceItemTotals
    )
    totals.total = totals.labor + totals.parts + totals.misc
    return totals
}
